import { isServer } from '@toss/utils';
import { type SupportedLocale } from '@zep-us/i18n';

import { type PaymentSource } from '@/module/payment';

/**
 * 결제 플로우 URL 상수
 * TossPayments successUrl, failUrl 등에서도 사용
 */
export const PAYMENT_URLS = {
  // 메인 플로우 페이지들
  PRICING: '/pricing',
  METHODS: '/checkout/method',
  CHECKOUT: '/checkout',
  PAYMENT: '/checkout/payment',
  PROCESSING: '/checkout/processing',
  COMPLETE: '/checkout/complete',

  getPaymentUrl: (preOrderId: string, paymentSource?: PaymentSource) => {
    const baseUrl = `/checkout/payment?preOrderId=${preOrderId}`;
    return paymentSource ? `${baseUrl}&source=${paymentSource}` : baseUrl;
  },
  getCheckoutUrl: (plan?: string) => {
    return plan ? `/checkout?plan=${plan}` : '/checkout';
  },
  getMethodsUrl: (plan?: string) => {
    return plan ? `/checkout/method?plan=${plan}` : '/checkout/method';
  },
} as const;

/**
 * TossPayments 콜백 API URL 생성.
 *
 * sync 플로우 (KRW 카드 / PayPal): Toss 가 `successUrl` 로 리다이렉트 → processing 페이지에서
 *   `useHandlePaymentSuccessSuspense` 가 서버 confirm 호출 후 complete 로 이동.
 *
 * async 플로우 (Antom 비동기): Toss 가 `pendingUrl` 로 리다이렉트 → processing 페이지에서
 *   `useFetchPaymentStatus` 가 폴링 → APPROVED 시 complete 로 이동.
 *
 * 둘 다 같은 `/checkout/processing` 페이지를 재사용하며, `flow` 쿼리 파라미터로 분기합니다.
 */
export const getTossPaymentsCallbackUrls = (
  preOrderId: string,
  locale: SupportedLocale,
  paymentSource?: PaymentSource,
) => {
  const baseUrl = isServer() ? '' : window.location.origin;
  const sourceQuery = paymentSource ? `&source=${paymentSource}` : '';
  const processingBase = `${baseUrl}/${locale}${PAYMENT_URLS.PROCESSING}?preOrderId=${preOrderId}${sourceQuery}`;
  return {
    successUrl: `${processingBase}&flow=sync`,
    pendingUrl: `${processingBase}&flow=async`,
    failUrl: `${baseUrl}/${locale}${PAYMENT_URLS.getPaymentUrl(
      preOrderId,
      paymentSource,
    )}`,
  };
};

/**
 * 현재 경로가 어느 결제 단계인지 확인
 */
export const getPaymentStepFromPath = (pathname: string) => {
  if (pathname.includes('/checkout/complete')) return 'complete';
  if (pathname.includes('/checkout/processing')) return 'processing';
  if (pathname.includes('/checkout/payment')) return 'payment';
  if (pathname.includes('/checkout/method')) return 'methods';
  if (
    pathname.includes('/checkout') &&
    !pathname.includes('/method') &&
    !pathname.includes('/payment') &&
    !pathname.includes('/processing')
  ) {
    return 'checkout';
  }
  if (pathname.includes('/pricing')) return 'pricing';
  return 'pricing';
};
